/**
 * Datums- und Monatslogik. Alle Rechnungen laufen über Kalendertage in UTC
 * (kein Tagesversatz durch Zeitzonen, NR-08). Zeitpunkte (Instants) werden nur
 * über eine ausdrücklich übergebene IANA-Zeitzone auf Tage/Monate abgebildet (KA-17).
 */
import { DomainError, type IsoDate, type MonthKey } from "./types.js";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;
const DAY_MS = 86_400_000;

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== "string") return false;
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === value;
}

export function assertIsoDate(value: string, field = "date"): IsoDate {
  if (!isIsoDate(value)) throw new DomainError("INVALID_DATE", `${field}: '${value}' ist kein Datum YYYY-MM-DD`);
  return value;
}

function toUtcMs(date: IsoDate): number {
  assertIsoDate(date);
  return Date.parse(`${date}T00:00:00Z`);
}

function fromUtcMs(ms: number): IsoDate {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Kalendertag + n Tage (n darf negativ sein). */
export function addDays(date: IsoDate, days: number): IsoDate {
  if (!Number.isFinite(days)) throw new DomainError("INVALID_DAYS", String(days));  // R-30
  return fromUtcMs(toUtcMs(date) + Math.trunc(days) * DAY_MS);
}

/** Tage von `from` bis `to` (positiv, wenn `to` später liegt). */
export function diffDays(from: IsoDate, to: IsoDate): number {
  return Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS);
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}

/** 0 = Sonntag … 6 = Samstag */
export function weekday(date: IsoDate): number {
  return new Date(toUtcMs(date)).getUTCDay();
}

export function isWeekend(date: IsoDate): boolean {
  const d = weekday(date);
  return d === 0 || d === 6;
}

export function isWorkingDay(date: IsoDate, holidays: readonly IsoDate[] = []): boolean {
  return !isWeekend(date) && !holidays.includes(date);
}

/** Nächster Werktag ab (einschließlich) `date`. */
export function nextWorkingDay(date: IsoDate, holidays: readonly IsoDate[] = []): IsoDate {
  let d = date;
  while (!isWorkingDay(d, holidays)) d = addDays(d, 1);
  return d;
}

/** `date` + n Werktage (Wochenenden und übergebene Feiertage übersprungen). */
export function addWorkingDays(date: IsoDate, days: number, holidays: readonly IsoDate[] = []): IsoDate {
  if (!Number.isFinite(days)) throw new DomainError("INVALID_DAYS", String(days));
  if (days < 0) throw new DomainError("NEGATIVE_DAYS");
  let d = date;
  let left = Math.trunc(days);
  // 0 Werktage: eine Frist endet nie an einem Wochenende oder Feiertag (R-30)
  if (left === 0) {
    while (!isWorkingDay(d, holidays)) d = addDays(d, 1);
    return d;
  }
  while (left > 0) {
    d = addDays(d, 1);
    if (isWorkingDay(d, holidays)) left -= 1;
  }
  return d;
}

export function addDaysByUnit(
  date: IsoDate,
  days: number,
  unit: "working" | "calendar",
  holidays: readonly IsoDate[] = [],
): IsoDate {
  return unit === "working" ? addWorkingDays(date, days, holidays) : addDays(date, days);
}

// ---------------------------------------------------------------------------
// Monate
// ---------------------------------------------------------------------------

export function isMonthKey(value: unknown): value is MonthKey {
  if (typeof value !== "string") return false;
  const m = MONTH_KEY.exec(value);
  return !!m && Number(m[2]) >= 1 && Number(m[2]) <= 12;
}

export function monthOf(date: IsoDate): MonthKey {
  assertIsoDate(date);
  return date.slice(0, 7);
}

export function firstDayOfMonth(month: MonthKey): IsoDate {
  if (!isMonthKey(month)) throw new DomainError("INVALID_MONTH", month);
  return `${month}-01`;
}

export function lastDayOfMonth(month: MonthKey): IsoDate {
  return addDays(firstDayOfMonth(addMonths(month, 1)), -1);
}

export function addMonths(month: MonthKey, n: number): MonthKey {
  if (!isMonthKey(month)) throw new DomainError("INVALID_MONTH", month);
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + Math.trunc(n);
  const year = y + Math.floor(m / 12);
  const mon = ((m % 12) + 12) % 12;
  return `${String(year).padStart(4, "0")}-${String(mon + 1).padStart(2, "0")}`;
}

/** Alle Monate von `start` bis `end` (beide eingeschlossen). */
export function monthRange(start: MonthKey, end: MonthKey): MonthKey[] {
  if (!isMonthKey(start) || !isMonthKey(end)) throw new DomainError("INVALID_MONTH");
  const out: MonthKey[] = [];
  let m = start;
  while (m <= end) {
    out.push(m);
    m = addMonths(m, 1);
  }
  return out;
}

/** Monatsliste einer Laufzeit ab Startdatum mit `months` Monaten. */
export function monthsForTerm(startDate: IsoDate, months: number): MonthKey[] {
  if (months <= 0) return [];
  const start = monthOf(startDate);
  return monthRange(start, addMonths(start, months - 1));
}

export function isDateInMonth(date: IsoDate, month: MonthKey): boolean {
  return monthOf(date) === month;
}

// ---------------------------------------------------------------------------
// Zeitzonen (KA-17, RE-02)
// ---------------------------------------------------------------------------

function parts(instant: string | Date, timeZone: string): { y: string; m: string; d: string } {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) throw new DomainError("INVALID_INSTANT", String(instant));
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    throw new DomainError("INVALID_TIME_ZONE", timeZone);
  }
  const p = fmt.formatToParts(date);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return { y: get("year"), m: get("month"), d: get("day") };
}

/** Kalendertag eines Zeitpunkts in der Zeitzone der Agentur. */
export function localDate(instant: string | Date, timeZone: string): IsoDate {
  const { y, m, d } = parts(instant, timeZone);
  return `${y}-${m}-${d}`;
}

/** Monatsschlüssel eines Zeitpunkts in der Zeitzone der Agentur. */
export function monthKeyInTimeZone(instant: string | Date, timeZone: string): MonthKey {
  const { y, m } = parts(instant, timeZone);
  return `${y}-${m}`;
}

// ---------------------------------------------------------------------------
// Fachliche Helfer
// ---------------------------------------------------------------------------

/**
 * Leistungsmonat (KA-20): gewählte Monatszeile, sonst Monat der Kampagne.
 */
export function resolveRevenueMonth(input: { monthLine?: MonthKey | null; dealMonth?: MonthKey | null }): MonthKey | null {
  if (input.monthLine && isMonthKey(input.monthLine)) return input.monthLine;
  if (input.dealMonth && isMonthKey(input.dealMonth)) return input.dealMonth;
  return null;
}

/**
 * Fairer Monatsvergleich (KZ-15): laufender Monat bis heute gegen den Vormonat bis zum
 * gleichen Kalendertag (gekappt auf das Monatsende des Vormonats).
 */
export function fairComparisonWindows(today: IsoDate): {
  current: { from: IsoDate; to: IsoDate };
  previous: { from: IsoDate; to: IsoDate };
} {
  const month = monthOf(today);
  const prev = addMonths(month, -1);
  const day = Number(today.slice(8, 10));
  const prevLast = lastDayOfMonth(prev);
  const prevDay = Math.min(day, Number(prevLast.slice(8, 10)));
  return {
    current: { from: firstDayOfMonth(month), to: today },
    previous: { from: firstDayOfMonth(prev), to: `${prev}-${String(prevDay).padStart(2, "0")}` },
  };
}

/**
 * Laufzeit eines Links/Tokens deckeln (WS-14): gilt beim Anlegen UND beim Verlängern.
 * `base` ist der Tag des Anlegens bzw. der Verlängerung.
 */
export function capExpiryDate(requested: IsoDate | null, base: IsoDate, maxDays: number): IsoDate {
  const cap = addDays(base, Math.max(0, Math.trunc(maxDays)));
  if (!requested) return cap;
  return requested > cap ? cap : requested;
}

/** Fälligkeit = Datum + Frist in Kalendertagen, gerechnet in UTC (NR-08). */
export function dueDateFrom(date: IsoDate, termDays: number): IsoDate {
  return addDays(date, termDays);
}

/**
 * Fälligkeit einer Rechnung mit Zahlungsziel in Tagen (Probelauf M1, 0109): EINE Regel für Datenbank (app.invoice_rule_due, dort
 * in document_due_date und effective_due_date gespeichert), Beleg, Mail, Mahnung und Oberfläche.
 *   Beginn = Zugang beim Empfänger (Tag in der Zeitzone der Agentur); steht er nicht fest, der Versandtag; vor dem Versand das
 *            Rechnungsdatum (vorläufig)
 *   Fälligkeit = Beginn + Zahlungsziel (Kalendertage)
 * Eine verlängerte Frist (invoice_extend_due) verschiebt nur die wirksame Fälligkeit. Ohne Zahlungsziel (Rechnungen von Creatorn,
 * Übernahmen aus dem bisherigen System) gilt das gespeicherte Datum; dann null.
 */
export function invoiceDueDate(input: { issueDate: IsoDate | null; sentOn?: IsoDate | null; deliveredOn?: IsoDate | null; paymentTermDays: number | null | undefined }): IsoDate | null {
  const term = input.paymentTermDays;
  if (typeof term !== "number" || !Number.isInteger(term) || term < 0) return null;
  const start = input.deliveredOn ?? input.sentOn ?? input.issueDate;
  return start ? dueDateFrom(start.slice(0, 10), term) : null;
}
